import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Fade,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SearchIcon from "@mui/icons-material/Search";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import InboxIcon from "@mui/icons-material/Inbox";
import { toast } from "react-toastify";

import PromotionTable from "../Components/promotions/PromotionTable";
import PromotionWizardModal from "../Components/promotions/PromotionWizardModal";
import { promotionsApi } from "../Components/promotionsApi";
import { getProgramCourses, getPrograms } from "../Components/enquiryProfileApi";
import { GlobalSearchBar } from "../Components/ERPList";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useTheme } from "../hooks/useTheme";
import { createMuiThemeFromMode } from "../theme/theme";

import styles from "./AllEnquiries.module.scss";
import { RoleContext } from "../Contexts/RoleContext";

const PAGE_SIZE_OPTIONS = [10, 12, 25, 50];

const FilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  backgroundColor: "#ffffff",
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const baseThemeOptions = {
  palette: {
    secondary: { main: "#f5365c" },
    success: { main: "#2dce89" },
    warning: { main: "#fb6340" },
    info: { main: "#11cdef" },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    h4: { fontWeight: 600, letterSpacing: "-0.02em" },
    button: { textTransform: "none", fontWeight: 500 },
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { boxShadow: "none" },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
      },
    },
  },
};

const defaultFilters = {
  status: "",
  program_id: "",
  course_id: "",
  start_from: "",
  start_to: "",
};

export default function PromotionsPage() {
  const { role } = useContext(RoleContext);
  const canApproveReject = role === "admin";
  const { themeMode } = useTheme();
  const theme = useMemo(
    () => createTheme(createMuiThemeFromMode(themeMode, baseThemeOptions)),
    [themeMode]
  );

  const [programs, setPrograms] = useState([]);
  const [courses, setCourses] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardMode, setWizardMode] = useState("create");
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [creating, setCreating] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const [globalSearch, setGlobalSearch] = useState("");
  const debouncedGlobalSearch = useDebouncedValue(globalSearch, 400);
  const [filterOpen, setFilterOpen] = useState(true);
  const [filterParams, setFilterParams] = useState(defaultFilters);
  const debouncedFilterParams = useDebouncedValue(filterParams, 400);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);

  const loadBaseData = useCallback(async () => {
    const programList = await getPrograms({});
    const normalizedPrograms = Array.isArray(programList) ? programList : [];
    setPrograms(normalizedPrograms);
    const courseLists = await Promise.all(
      normalizedPrograms.map(async (p) => {
        try {
          const list = await getProgramCourses(p.id);
          return (Array.isArray(list) ? list : []).map((c) => ({ ...c, program_id: p.id }));
        } catch {
          return [];
        }
      })
    );
    setCourses(courseLists.flat());
  }, []);

  const loadPromotions = useCallback(async () => {
    const data = await promotionsApi.list();
    setPromotions(data?.promotions || []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadBaseData(), loadPromotions()]);
      } catch (error) {
        if (!cancelled) toast.error(error?.message || "Failed to load promotions.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadBaseData, loadPromotions]);

  const programsById = useMemo(
    () => Object.fromEntries(programs.map((p) => [p.id, p])),
    [programs]
  );
  const coursesById = useMemo(
    () => Object.fromEntries(courses.map((c) => [c.id, c])),
    [courses]
  );

  const coursesForFilterProgram = useMemo(() => {
    const pid = debouncedFilterParams.program_id;
    if (!pid) return courses;
    return courses.filter((c) => String(c.program_id) === String(pid));
  }, [courses, debouncedFilterParams.program_id]);

  const filteredPromotions = useMemo(() => {
    let list = [...promotions];
    const q = debouncedGlobalSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const name = (p.name || "").toLowerCase();
        const idStr = String(p.id);
        const targetName = p.course_id
          ? (coursesById[p.course_id]?.name || "").toLowerCase()
          : (programsById[p.program_id]?.name || "").toLowerCase();
        const status = (p.status || "").toLowerCase();
        return (
          name.includes(q) ||
          idStr.includes(q) ||
          targetName.includes(q) ||
          status.includes(q)
        );
      });
    }

    const f = debouncedFilterParams;
    if (f.status) list = list.filter((p) => p.status === f.status);
    if (f.program_id) {
      list = list.filter(
        (p) =>
          String(p.program_id) === String(f.program_id) ||
          (p.course_id && String(coursesById[p.course_id]?.program_id) === String(f.program_id))
      );
    }
    if (f.course_id) list = list.filter((p) => String(p.course_id) === String(f.course_id));

    if (f.start_from) {
      const from = new Date(f.start_from);
      from.setHours(0, 0, 0, 0);
      list = list.filter((p) => {
        if (!p.start_date) return false;
        return new Date(p.start_date) >= from;
      });
    }
    if (f.start_to) {
      const to = new Date(f.start_to);
      to.setHours(23, 59, 59, 999);
      list = list.filter((p) => {
        if (!p.start_date) return false;
        return new Date(p.start_date) <= to;
      });
    }

    list.sort((a, b) => {
      const ta = new Date(a.created_at || 0).getTime();
      const tb = new Date(b.created_at || 0).getTime();
      return tb - ta;
    });
    return list;
  }, [promotions, debouncedGlobalSearch, debouncedFilterParams, coursesById, programsById]);

  const totalCount = filteredPromotions.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize) || 1);
  const safePage = Math.min(currentPage, totalPages);
  const pageSlice = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredPromotions.slice(start, start + pageSize);
  }, [filteredPromotions, safePage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedGlobalSearch, debouncedFilterParams, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    setFilterParams((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "program_id") next.course_id = "";
      return next;
    });
  };

  const resetFilters = () => {
    setFilterParams(defaultFilters);
    setGlobalSearch("");
  };

  const createPromotion = async (payload) => {
    setCreating(true);
    try {
      if (wizardMode === "edit" && editingPromotion?.id) {
        await promotionsApi.update(editingPromotion.id, payload);
        toast.success("Promotion updated.");
      } else {
        await promotionsApi.create(payload);
        toast.success("Promotion created.");
      }
      setWizardOpen(false);
      setWizardMode("create");
      setEditingPromotion(null);
      await loadPromotions();
    } catch (error) {
      throw error;
    } finally {
      setCreating(false);
    }
  };

  const doAction = async (action, promotionId, successMessage) => {
    setActionLoadingId(promotionId);
    try {
      await action(promotionId);
      toast.success(successMessage);
      await loadPromotions();
    } catch (error) {
      toast.error(error?.message || "Action failed.");
    } finally {
      setActionLoadingId(null);
    }
  };

  const openNewPromotion = () => {
    setWizardMode("create");
    setEditingPromotion(null);
    setWizardOpen(true);
  };

  const openEditPromotion = (promotion) => {
    setWizardMode("edit");
    setEditingPromotion(promotion);
    setWizardOpen(true);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className={styles.root}>
        <Paper elevation={0} className={styles.contentPaper}>
          <Box sx={{ marginBottom: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box>
                <Typography variant="h4" className={styles.pageTitle}>
                  Promotions
                </Typography>
                <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                  Create, filter, and manage program or course discounts.
                  {!canApproveReject ? " Approvals are done by administrators." : ""}
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Box className={styles.header}>
            <GlobalSearchBar
              value={globalSearch}
              onChange={setGlobalSearch}
              placeholder="Search name, ID, target, status…"
            />
            <Box className={styles.headerActions}>
              <Button
                startIcon={<FilterListIcon />}
                onClick={() => setFilterOpen(!filterOpen)}
                variant="outlined"
                size="small"
              >
                {filterOpen ? "Hide filters" : "Show filters"}
              </Button>
              <Button
                variant="contained"
                startIcon={<LocalOfferIcon />}
                onClick={openNewPromotion}
              >
                New promotion
              </Button>
            </Box>
          </Box>

          {filterOpen ? (
            <Fade in={filterOpen}>
              <FilterPaper variant="outlined">
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        label="Status"
                        value={filterParams.status}
                        onChange={handleFilterChange("status")}
                      >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="draft">Draft</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="finished">Finished</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Program</InputLabel>
                      <Select
                        label="Program"
                        value={filterParams.program_id}
                        onChange={handleFilterChange("program_id")}
                      >
                        <MenuItem value="">All</MenuItem>
                        {programs.map((p) => (
                          <MenuItem key={p.id} value={String(p.id)}>
                            {p.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Course</InputLabel>
                      <Select
                        label="Course"
                        value={filterParams.course_id}
                        onChange={handleFilterChange("course_id")}
                      >
                        <MenuItem value="">All</MenuItem>
                        {coursesForFilterProgram.map((c) => (
                          <MenuItem key={c.id} value={String(c.id)}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Start date from"
                      type="date"
                      value={filterParams.start_from}
                      onChange={(e) => setFilterParams((prev) => ({ ...prev, start_from: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Start date to"
                      type="date"
                      value={filterParams.start_to}
                      onChange={(e) => setFilterParams((prev) => ({ ...prev, start_to: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} className={styles.filterActions}>
                    <Button variant="contained" startIcon={<SearchIcon />} onClick={() => setCurrentPage(1)}>
                      Apply
                    </Button>
                    <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={resetFilters}>
                      Reset
                    </Button>
                  </Grid>
                </Grid>
              </FilterPaper>
            </Fade>
          ) : null}

          {loading ? (
            <Box className={styles.loadingContainer}>
              <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={420} sx={{ borderRadius: 2 }} />
            </Box>
          ) : null}

          {!loading && pageSlice.length === 0 ? (
            <Box className={styles.emptyState}>
              <InboxIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No promotions match your filters
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting search or filters, or create a new promotion.
              </Typography>
            </Box>
          ) : null}

          {!loading && pageSlice.length > 0 ? (
            <>
              <Box className={styles.pagination}>
                <Typography variant="body2" color="text.secondary">
                  Showing {totalCount === 0 ? 0 : (safePage - 1) * pageSize + 1}–
                  {Math.min(safePage * pageSize, totalCount)} of {totalCount} results
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Per page</InputLabel>
                    <Select
                      value={pageSize}
                      label="Per page"
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {PAGE_SIZE_OPTIONS.map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Pagination
                    count={totalPages}
                    page={safePage}
                    onChange={(_, page) => setCurrentPage(page)}
                    color="primary"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              </Box>

              <PromotionTable
                promotions={pageSlice}
                programsById={programsById}
                coursesById={coursesById}
                actionLoadingId={actionLoadingId}
                canApproveReject={canApproveReject}
                showTitle={false}
                paperClassName={styles.tableContainer}
                tableClassName={styles.enquiriesTable}
                onSubmit={(id) => doAction(promotionsApi.submit, id, "Promotion submitted.")}
                onApprove={(id) => doAction(promotionsApi.approve, id, "Promotion approved.")}
                onReject={(id) => doAction(promotionsApi.reject, id, "Promotion rejected.")}
                onEdit={openEditPromotion}
              />
            </>
          ) : null}
        </Paper>
      </Box>

      <PromotionWizardModal
        show={wizardOpen}
        onHide={() => {
          if (creating) return;
          setWizardOpen(false);
          setWizardMode("create");
          setEditingPromotion(null);
        }}
        programs={programs}
        courses={courses}
        onSubmit={createPromotion}
        submitting={creating}
        mode={wizardMode}
        initialData={editingPromotion}
      />
    </ThemeProvider>
  );
}
